import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { CustomToolbarSelect, ConfirmDialog } from 'components';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grow,
  Icon,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@material-ui/core';
import {
  EditIcon,
  DeleteIcon,
} from 'components/CustomSVG';
import MUIDataTable from 'mui-datatables';
import {
  getEditableStoreProductApi,
  getStoreProductExportApi,
  addTasksToQueueApi,
  getStoreTaxClassesApi
} from 'api';
import logo from 'components/Logo/logo-dermpro.svg';
import { generateTaskToDeleteProduct } from 'helpers/queueTasks';
import { imgBaseUrl } from 'configs';


const useStyles = makeStyles(() => ({
  root: {
    '& .MuiTableHead-root': {
      position: 'sticky'
    },
    '& > .MuiToolbar-root': {
      borderTop: '1px solid #D0E9FF'
    },
    '& .MuiTableCell-head, & .MuiTableCell-head .MuiButton-root': {
      color: '#4277C6',
      textTransform: 'uppercase',
      fontSize: '12px',
      fontWeight: 600
    },
    '& .MuiTableCell-head': {
      borderColor: '#D0E9FF',
    },
    '& .MuiButton-label *': {
      color: '#4277C6'
    },
    '& .MuiTableCell-body': {
      color: '#777',
      fontWeight: 500
    },
    '& .MuiCheckbox-root': {
      color: '#4B6FC2'
    },
    '& .MuiTableFooter-root': {
      background: '#E7F0FF',
      color: '#4B6FC2',
      '& .MuiSelect-icon, & .MuiTablePagination-root': {
        color: '#4B6FC2'
      },
      '& .MuiIconButton-root.Mui-disabled': {
        color: '#4B6FC2',
        opacity: '0.44'
      }
    }
  }
}));

const displayStatus = {
  'CREATE_PRODUCT': 'Creating...',
  'UPDATE_PRODUCT_TO_ADD_STOREVIEW': 'Creating...',
  'UPDATE_PRODUCT': 'Updating...',
  'DELETE_PRODUCT': 'Deleting...',
  'UPDATE_PRODUCT_TO_DELETE_STOREVIEW': 'Deleting...'
};

const StoreProductsList = ({ productsList, queueTasks, queueTasksSkus }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const location = useLocation();

  const [catalog, setCatalog] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [productToDelete, setProductDelete] = useState(null);
  const [giftCardEditBlocked, setGiftCardEditBlocked] = useState(false);
  const [viewColumns, setViewColumns] = useState({
    'name': true,
    'qty': true,
    'brand': true,
    'price': true,
    'subscription_active': false,
    'isTaxable': false,
    'status': true,
    'action': true
  });

  const { storeBrands } = useSelector((state: IReduxState) => state.brands);
  const {storeProductsExport, storeTaxClasses, loadingProductsExport} = useSelector((state: IReduxState) => state.products);
  const {profile} = useSelector((state: IReduxState) => state.user);
  const taxableClass = storeTaxClasses.find((c) => c.class_name.toLowerCase().includes(profile?.stores?.[0]?.storeCode));

  const searchParams = new URLSearchParams(location.search);
  const currentPage = searchParams.get('page');
  const rowsPerPage = searchParams.get('rowsPerPage');

  const permissions = profile?.config?.permissions || {};
  const userStore = profile?.stores?.[0];
  const mediaStoreUrl = `${userStore?.apiUrl}/media/catalog/product` || imgBaseUrl;
  const isQueueWorks = queueTasks.find((queue) => queue.status !== 'FAILED');
  const productsType = location.pathname.includes('active') ? 'active' : 'draft';

  useEffect(() => {
    if (!isQueueWorks) {
      getStoreProductExportApi({ dispatch });
    }
  }, [isQueueWorks]);

  useEffect(() => {
    getStoreTaxClassesApi({dispatch});
  }, []);

  useEffect(() => {
    if (!searchParams.has('page')) {
      searchParams.set('page', '0');

      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }

    if (!searchParams.has('rowsPerPage')) {
      searchParams.set('rowsPerPage', '20');

      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  }, [currentPage]);

  useEffect(() => {
    if (storeBrands?.length) {
      setCatalog([...productsList.map((product) => {
        const productBrand = product?.custom_attributes?.find((attr) => attr.attribute_code === 'brand')?.value;
        const productImagePath = product?.custom_attributes?.find((attr) => attr.attribute_code === 'image')?.value;
        const productTaxClassId = product?.custom_attributes?.find((attr) => attr.attribute_code === 'tax_class_id')?.value || '';

        return {
          ...product,
          image: productImagePath ? `${mediaStoreUrl}${productImagePath}` : logo,
          price: +Number.parseFloat(product?.price).toFixed(2) || '0',
          brand: storeBrands?.find((b) => b.option_id === productBrand)?.value ||
            product?.custom_attributes?.find((attr) => attr.attribute_code === 'catalog_brand_name')?.value,
          status: product?.status === 1 ? 'Enabled' : 'Disabled',
          subscription_active: product?.custom_attributes?.find((attr) => attr.attribute_code === 'subscription_active')?.value || '0',
          isTaxable: productTaxClassId === String(taxableClass?.class_id) ? 1 : 0,
          qty: product?.extension_attributes?.stock_item?.qty
        };
      })]);
    }
  }, [productsList.length, storeBrands, queueTasksSkus.length, taxableClass]);

  const openProductEdit = (sku = 'Product SKU', brand = 'Product Brand') => {
    getEditableStoreProductApi({dispatch, sku});
    history.push({pathname: `/store-products/edit/${sku}`, search: `?brand=${brand}`});
  };

  const handleChangeProductStatus = (product) => {
    const editedProduct = { extension_attributes: product.extension_attributes, sku: product.sku, status: 2 };

    const task = {
      body: editedProduct,
      name: product.name,
      sku: product.sku,
      requestType: 'UPDATE_PRODUCT',
    };
    addTasksToQueueApi({ dispatch, tasks: [task] });
  };

  const handleConfirmDelete = () => {
    const task = generateTaskToDeleteProduct(
      productToDelete,
      userStore?.quoteProStoreId,
      userStore.storeId,
      userStore.storeCode
    );

    addTasksToQueueApi({dispatch, tasks: [task]});
  };

  const columns = [
    {
      name: 'name',
      label: 'Item',
      options: {
        sort: true,
        filter: true,
        display: viewColumns['name'],
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <div className="flex items-center">
              <img
                src={loaded ? product.image : logo}
                alt={product.name}
                style={{maxHeight: '60px', width: '60px', objectFit: 'contain'}}
                className="border-radius-8"
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                  (e.target as HTMLImageElement).onerror = null;
                  (e.target as HTMLImageElement).src = logo;
                }}
              />
              <div className="ml-3">
                <p className="my-0 text-15" >{product.name}</p>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'qty',
      label: 'Inventory',
      options: {
        filter: true,
        sort: true,
        display: viewColumns['qty'],
        setCellHeaderProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          const { qty, type_id} = product;
          return (
            <div className="flex items-center">
              <p className="my-0 text-15 capitalize">{type_id === 'simple' && qty}</p>
            </div>
          );
        },
      },
    },
    {
      name: 'brand',
      label: 'Brand',
      options: {
        filter: true,
        sort: true,
        display: viewColumns['brand'],
        setCellHeaderProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          const { brand } = product;

          return (
            <div className="flex items-center">
              <p className="my-0 text-15 capitalize">{brand}</p>
            </div>
          );
        },
      },
    },
    {
      name: 'price',
      label: 'Price',
      options: {
        filter: true,
        sort: true,
        display: viewColumns['price'],
        setCellHeaderProps: () => ({ style: { width: '10%' }, className: 'whitespace-pre'}),
        setCellProps: () => ({className: 'whitespace-pre'}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <div className="flex items-center">
              {`$${Number(product.price) ? product.price.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') : product.price}`}
            </div>
          );
        },
      },
    },
    {
      name: 'subscription_active',
      label: 'Is Subscription',
      options: {
        filter: true,
        sort: true,
        display: viewColumns['subscription_active'],
        filterType: 'dropdown',
        filterOptions: {
          renderValue: (v) => v == 1 ? 'Yes' : 'No'
        },
        customFilterListOptions: {
          render: (v) => v == 1 ? 'Yes' : 'No'
        },
        setCellHeaderProps: () => ({ style: { width: '10%', whiteSpace: 'nowrap' }, className: 'text-center max-w-100 w-full hyphens pr-2' }),
        setCellProps: () => ({className: 'text-center max-w-100 w-full'}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <>
              {product.subscription_active == 1 ?
                <Icon className="text-primary">check_box</Icon>
                :
                <Icon className="text-muted">check_box_outline_blank_icon</Icon>
              }
            </>
          );
        },
      },
    },
    {
      name: 'isTaxable',
      label: 'Is Taxable',
      options: {
        filter: false,
        sort: true,
        display: viewColumns['custom_attributes'],
        setCellHeaderProps: () => ({ style: { width: '10%', whiteSpace: 'nowrap' }, className: 'text-center max-w-100 w-full' }),
        setCellProps: () => ({className: 'text-center max-w-100 w-full'}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];

          return (
            <>
              {product.isTaxable === 1 ?
                <Icon className="text-primary">check_box</Icon>
                :
                <Icon className="text-muted">check_box_outline_blank_icon</Icon>
              }
            </>
          );
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: false,
        sort: true,
        display: viewColumns['status'],
        filterType: 'dropdown',
        setCellHeaderProps: () => ({ style: { width: '10%'}}),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex];
          return (
            <Chip label={product.status} color={product.status === 'Enabled' ? 'primary' : 'default'} />
          );
        },
      },
    },
    {
      name: 'action',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        display: viewColumns['action'],
        setCellHeaderProps: () => ({ className: 'w-120' }),
        customBodyRenderLite: (dataIndex) => {
          const product = catalog[dataIndex] || {};
          const isQueued = queueTasksSkus.find((sku) => sku === product.sku);

          if (isQueued) {
            const task = queueTasks.find((t) => t.sku === product.sku);

            if (!task.error) {
              return (
                <div className="flex items-center">
                  {displayStatus[task.requestType]}
                </div>
              );
            }
          }

          const handleDeleteClick = (product) => {
            if (product.status === 'Enabled') {
              handleChangeProductStatus(product);
            } else {
              setProductDelete(product);
            }
          };

          return (
            <div className="flex items-center">
              <IconButton
                title="Click here to make edits to your item (e.g.: name, price, options, subscription, etc.)"
                disabled={isQueued}
                onClick={() => {
                  const isRangeAllowed = product.custom_attributes?.find((a) => a.attribute_code === 'allow_amount_range')?.value === '1';

                  if (product.type_id === 'mpgiftcard' && !isRangeAllowed) {
                    setGiftCardEditBlocked(true);
                    return;
                  }

                  openProductEdit(product?.sku, product?.brand);
                }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                title="Click here to delete the product from your store"
                disabled={isQueued}
                onClick={() => handleDeleteClick(product)}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          );
        },
      },
    },
  ];

  return (
    <>
      <MUIDataTable
        className={classes.root}
        data={catalog}
        columns={columns}
        options={{
          filterArrayFullMatch: false,
          filterType: 'textField',
          responsive: 'standard',
          selectableRows: permissions.massEditActive ? 'multiple' : 'none',
          search: true,
          filter: true,
          download: loadingProductsExport || isQueueWorks  ? 'disabled' : profile.isDermproStaff,
          downloadOptions: {
            filename: 'sm-listings.csv'
          },
          textLabels: {
            toolbar: {
              downloadCsv: 'Click here to download your current SKUs in an Excel spreadsheet'
            }
          },
          onDownload: (buildHead, buildBody, columns) => {
            const data = storeProductsExport.products;
            const columnLength = columns.length;

            // deleting grid columns
            for (let i = 0; i < columnLength; i++) {
              columns.shift();
            }

            storeProductsExport.columns.forEach((column) => {
              columns.push({
                download: true,
                name: column,
                label: column
              });
            });

            return `${buildHead(columns)}${buildBody(data)}`;
          },
          print: false, // set print option
          pagination: true, //set pagination option
          viewColumns: true, // set column option
          elevation: 0,
          searchPlaceholder: 'Enter product name',
          rowsPerPage: Number(rowsPerPage),
          rowsPerPageOptions: [20, 40, 80, 100],
          page: Number(currentPage),
          onChangePage: (page) => {
            history.push({pathname: location.pathname, search: `?page=${page}&rowsPerPage=${rowsPerPage}`});
          },
          onChangeRowsPerPage: (rows) => {
            history.push({pathname: location.pathname, search: `?page=0&rowsPerPage=${rows}`});
          },
          onViewColumnsChange: (changedColumn: string, action: string) => {
            setViewColumns((prev) => ({...prev, [changedColumn]: action === 'add'}));
          },
          isRowSelectable: (dataIndex) => {
            const isQueued = queueTasksSkus.includes(catalog[dataIndex].sku);
            return !isQueued;
          },
          customToolbarSelect: (selectedRows) => (
            <CustomToolbarSelect
              selectedRows={selectedRows}
              productList={productsList}
              showActions={{ massEdit: true }}
              productsType={productsType}
            />
          ),
          customSearchRender: (
            searchText,
            handleSearch,
            hideSearch
          ) => {
            return (
              <Grow appear in timeout={300}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  onChange={({ target: { value } }) =>
                    handleSearch(value)
                  }
                  InputProps={{
                    style: {
                      paddingRight: 0,
                    },
                    startAdornment: (
                      <Icon
                        className="mr-2"
                        fontSize="small"
                      >
                        search
                      </Icon>
                    ),
                    endAdornment: (
                      <IconButton
                        onClick={hideSearch}
                      >
                        <Icon fontSize="small">
                          clear
                        </Icon>
                      </IconButton>
                    ),
                  }}
                />
              </Grow>
            );
          },
          onTableChange: (action, state) => {
            if (action === 'propsUpdate') {
              state.selectedRows = {
                data: [],
                lookup: {},
              };
            }
          },
        }}
      />
      <ConfirmDialog
        title="Delete product?"
        open={Boolean(productToDelete)}
        setOpen={setProductDelete}
        onConfirm={handleConfirmDelete}
      >
        Are you sure you want to delete the product from your store?
      </ConfirmDialog>
      <Dialog open={giftCardEditBlocked} onClose={() => setGiftCardEditBlocked(false)}>
        <DialogTitle>Editing Not Available</DialogTitle>
        <DialogContent>
          This gift card product cannot be edited because it is configured as a custom gift card with the amount range option disabled. Editing is only supported for gift cards with an amount range enabled.
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setGiftCardEditBlocked(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StoreProductsList;
